import { Search, Button, InlineNotification } from '@carbon/react';
import { Connect } from '@carbon/react/icons';
import { FormSection } from 'components/forms';
import { useAtom, useAtomValue } from 'jotai';
import { debounce } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { EntityResponse } from 'types/api-responses';

import { formatMutationResource } from 'utils/misc';
import { accountEnumsLoadableAtom, crmAccountsAtom } from './atoms';
import { CRMAccountsTable } from './crm-account-table';
import style from './crm-accounts.module.scss';
import { LinkCrmAccountModal } from './link-crm-account-modal';

interface SearchParams {
  page: number;
  pageSize: number;
  text: string;
  sortAttribute: string;
  sortDirection: string;
}

/**
 * An entity's linked CRM accounts, with search, linking and removal.
 * @param {object} props - The entity
 * @param {EntityResponse} props.entity - The entity whose accounts are shown
 * @return {JSX.Element} The CRM accounts section
 */
function CRMAccounts({ entity }: { entity: EntityResponse }) {
  const [searchText, setSearchText] = useState('');
  const [searchPayload, setSearchPayload] = useState<SearchParams>({
    pageSize: 50,
    page: 1,
    text: '',
    sortAttribute: 'name',
    sortDirection: 'ASC',
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [notificationType, setNotificationType] = useState<
    'link' | 'delete' | null
  >(null);

  const [crmAccountData, setCRMAccountsData] = useAtom(crmAccountsAtom);
  const { data: accountsData } = formatMutationResource(crmAccountData);

  // Fetch enums for account type and source
  const accountEnums = useAtomValue(accountEnumsLoadableAtom);
  const accountTypeOptions =
    accountEnums.state === 'hasData' && accountEnums.data
      ? accountEnums.data.accountType
      : [];
  const accountSourceOptions =
    accountEnums.state === 'hasData' && accountEnums.data
      ? accountEnums.data.accountSource
      : [];

  // Fetch CRM accounts
  const fetchCRMAccounts = useCallback(
    (params: SearchParams) => {
      const crmAccountsHref = entity.resources.crmAccounts?.href;
      if (!crmAccountsHref) return;

      setCRMAccountsData({
        url: crmAccountsHref,
        type: 'POST',
        data: {
          query: {
            entityId: entity.id,
            page: params.page,
            pageSize: params.pageSize,
            sortAttribute: params.sortAttribute,
            sortDirection: params.sortDirection,
            ...(params.text && { text: params.text }),
          },
        },
      });
    },
    [entity.id, entity.resources, setCRMAccountsData]
  );

  // Load initial data on mount
  useEffect(() => {
    fetchCRMAccounts(searchPayload);
  }, [fetchCRMAccounts, searchPayload]);

  // Debounced search function
  const debouncedSearch = useMemo(
    () =>
      debounce((searchValue: string) => {
        const newParams = {
          ...searchPayload,
          page: 1,
          text: searchValue,
        };
        setSearchPayload(newParams);
        fetchCRMAccounts(newParams);
      }, 400),
    [searchPayload, fetchCRMAccounts]
  );

  const handleLinkSuccess = useCallback(() => {
    fetchCRMAccounts(searchPayload);
    setNotificationType('link');
  }, [fetchCRMAccounts, searchPayload]);

  const handleDeleteSuccess = useCallback(() => {
    fetchCRMAccounts(searchPayload);
    setNotificationType('delete');
  }, [fetchCRMAccounts, searchPayload]);

  /** Opens the link modal, hiding any earlier notification. */
  const handleOpenModal = () => {
    setIsModalOpen(true);
    setNotificationType(null);
  };

  /** Closes the link modal. */
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const hasAccounts =
    accountsData?.results &&
    Array.isArray(accountsData.results) &&
    accountsData.results.length > 0;

  return (
    <FormSection title="CRM Accounts">
      {notificationType && (
        <div className={style.notificationContainer}>
          <InlineNotification
            kind="success"
            title={
              notificationType === 'delete'
                ? 'Removed CRM Account from Insights Entity'
                : 'Successfully linked CRM Account to Entity'
            }
            hideCloseButton={false}
            lowContrast
            onClose={() => {
              setNotificationType(null);
              return true;
            }}
          />
        </div>
      )}
      {(hasAccounts || searchText.length > 0) && (
        <div
          className={
            hasAccounts ? style.headerContainer : style.searchContainer
          }
        >
          <div className={style.searchContainer}>
            <Search
              id="crm-accounts-search"
              labelText="Search"
              placeholder="Search CRM Accounts"
              onChange={({ target: { value } }) => {
                setSearchText(value);
                debouncedSearch(value);
              }}
              value={searchText}
              className={style.searchInput}
            />
          </div>
          {hasAccounts && (
            <div className={style.manageButtonContainer}>
              <Button
                size="sm"
                renderIcon={Connect}
                iconDescription="Link CRM Account"
                onClick={handleOpenModal}
              >
                Link CRM Account
              </Button>
            </div>
          )}
        </div>
      )}
      <CRMAccountsTable
        crmAccountsData={crmAccountData}
        onLinkAccount={handleOpenModal}
        entity={entity}
        onDeleteSuccess={handleDeleteSuccess}
      />
      <LinkCrmAccountModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        entityId={entity.id}
        entity={entity}
        accountTypeOptions={accountTypeOptions}
        accountSourceOptions={accountSourceOptions}
        onLinkSuccess={handleLinkSuccess}
        initialSearchText={entity.name}
        existingLinkedAccounts={accountsData?.results ?? []}
      />
    </FormSection>
  );
}

export { CRMAccounts };
